'use client';

import {
  FilterChipToggleLeadingIconSlot,
  FilterChipToggleSpinnerSlot,
  FilterChipToggleTrailingIconSlot,
} from '@magicschool/spellbook/components/FilterChipToggle/slots';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Text } from '@magicschool/spellbook/components/Text';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getButtonTextVariant, getControlIconSize } from '@magicschool/spellbook/lib/controls';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { memo, useState } from 'react';

export type FilterChipToggleProps = React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof filterChipToggleVariants> & {
    /** If true, the button will display a loading spinner icon and click functionality will be disabled. */
    isLoading?: boolean;
    /** If true, a check icon will be displayed when the button is pressed. It will replace the leading icon slot (only when pressed). */
    showCheckOnPressed?: boolean;
  };

export const filterChipToggleVariants = cva(
  [
    'cursor-pointer',
    'inline-flex',
    'items-center',
    'justify-center',
    'whitespace-nowrap',
    'transition-colors',
    'shrink-0',
    '[&_svg]:shrink-0',
    'outline-none',
    'focus-visible-ring',
    'border-1',
    'border-transparent',
    'overflow-hidden',
    'text-ellipsis',
    'aria-busy:pointer-events-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:shadow-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:transition-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:focus-visible:ring-0',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:cursor-not-allowed',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:[&_svg]:text-disabled-foreground',
    'data-[state=on]:[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
    'data-[state=on]:[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
    'data-[state=on]:[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
    'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
    'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
    'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
    'text-default-text',
    'bg-surface-background-0',
    '[&_svg]:text-default-icon',
    'hover:[&_svg]:text-default-icon-hover',
    'hover:bg-surface-background-2',
    'active:bg-surface-background-3',
    'active:text-default-text-strong',
    /**
     * @TODO [SPE-16] Centralize “Selected” Color Tokens into Shared Style Utilities
     * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
     */
    'data-[state=on]:bg-selected-default-background',
    'data-[state=on]:hover:bg-selected-default-background-hover',
    'data-[state=on]:active:bg-selected-default-background-active',
    'data-[state=on]:[&_svg]:text-selected-default-icon',
    'data-[state=on]:hover:[&_svg]:text-selected-default-icon-hover',
    'data-[state=on]:active:[&_svg]:text-selected-default-icon-active',
    'data-[state=on]:text-selected-default-foreground',
    'data-[state=on]:hover:text-selected-default-foreground-hover',
    'data-[state=on]:active:text-selected-default-foreground-active',
    'aria-pressed:bg-selected-default-background',
    'aria-pressed:hover:bg-selected-default-background-hover',
    'aria-pressed:active:bg-selected-default-background-active',
    'aria-pressed:[&_svg]:text-selected-default-icon',
    'aria-pressed:hover:[&_svg]:text-selected-default-icon-hover',
    'aria-pressed:active:[&_svg]:text-selected-default-icon-active',
    'aria-pressed:text-selected-default-foreground',
    'aria-pressed:hover:text-selected-default-foreground-hover',
    'aria-pressed:active:text-selected-default-foreground-active',
    'border',
    'border-border-strong',
    'hover:border-border-strong-hover',
    'active:border-border-strong-hover',
    'data-[state=on]:border-selected-default-stroke',
    'aria-pressed:border-selected-default-stroke',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
  ],
  {
    variants: {
      /** The size of the button. */
      size: {
        xs: ['h-control-h-xs', 'gap-0', 'px-1', 'py-1', 'min-w-7', 'rounded-control-sm'],
        sm: ['h-control-h-sm', 'gap-0.5', 'px-1.5', 'py-1', 'min-w-7', 'rounded-control-sm'],
        md: ['h-control-h-md', 'gap-1', 'px-2', 'py-2', 'min-w-14', 'rounded-control-md'],
        lg: ['h-control-h-lg', 'gap-1', 'px-3', 'py-3', 'min-w-20', 'rounded-control-lg'],
      },
      /** Whether the button should take up the full width of its container. */
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      /** The alignment of the button's content. */
      justify: {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
      },
    },
  },
);

type FilterChipToggleSlottedContentProps = {
  slots: {
    loadingIcon?: React.ReactNode;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
  };
  rest?: React.ReactNode;
  isLoading?: boolean;
  pressed?: FilterChipToggleProps['pressed'];
  size: FilterChipToggleProps['size'];
  showCheckOnPressed?: FilterChipToggleProps['showCheckOnPressed'];
};

const FilterChipToggleSlottedContent = memo<FilterChipToggleSlottedContentProps>(
  ({ slots, rest, isLoading, pressed, size, showCheckOnPressed }) => {
    const iconSize = getControlIconSize(size);
    const textVariant = getButtonTextVariant(size);
    return (
      <>
        {isLoading ? (
          slots.loadingIcon
        ) : (
          <>{pressed && showCheckOnPressed ? <Icon name="check" color="inherit" size={iconSize} /> : slots.leadingIcon}</>
        )}
        <Text variant={textVariant} className="px-1 truncate" element="span">
          {rest}
        </Text>
        {slots.trailingIcon}
      </>
    );
  },
);

const FilterChipToggleBase: React.FC<FilterChipToggleProps> = ({
  children,
  className,
  disabled,
  fullWidth = false,
  isLoading = false,
  defaultPressed,
  pressed,
  onPressedChange,
  onClick,
  justify = 'center',
  size = 'md',
  showCheckOnPressed = true,
  ...props
}) => {
  const { slots, rest } = useSlots(children, {
    loadingIcon: { Component: FilterChipToggleSpinnerSlot, props: { size } },
    leadingIcon: { Component: FilterChipToggleLeadingIconSlot, props: { size } },
    trailingIcon: { Component: FilterChipToggleTrailingIconSlot, props: { size } },
  });
  const [internalPressed, setInternalPressed] = useState<boolean>(defaultPressed ?? false);
  const isControlled = pressed !== undefined;
  const isPressed = isControlled ? pressed : internalPressed;

  const isActuallyDisabled = disabled || isLoading;

  const handleOnPressedChange = (newPressed: boolean) => {
    if (!isControlled) {
      setInternalPressed(newPressed);
    }
    onPressedChange?.(newPressed);
  };

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isActuallyDisabled) {
      // explicitly prevent default and stop propagation
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    onClick?.(event);
  };

  return (
    <TogglePrimitive.Root
      data-spellbook-name="filter-chip-toggle"
      className={cn(filterChipToggleVariants({ size, fullWidth, justify }), className)}
      defaultPressed={defaultPressed}
      pressed={isPressed}
      onPressedChange={handleOnPressedChange}
      onClick={handleOnClick}
      type="button"
      aria-disabled={isActuallyDisabled}
      aria-busy={isLoading}
      {...props}
    >
      <FilterChipToggleSlottedContent
        slots={slots}
        rest={rest}
        isLoading={isLoading}
        pressed={isPressed}
        size={size}
        showCheckOnPressed={showCheckOnPressed}
      />
    </TogglePrimitive.Root>
  );
};

/**
 * A toggle chip is a chip that can be in one of two states: `on` or `off`, meant to be used as a boolean toggle for filters or options.
 *
 * ### When to use
 * - A boolean filter or option shown as a chip in a filter bar
 *
 * ### When NOT to use
 * - Choosing among several options: use `FilterChipSelect`
 * - A settings switch outside a filter bar: use `Switch` or `ToggleButton`
 *
 * ### Available slots
 * - `<FilterChipToggle.LeadingIcon>`: (optional) Renders a leading icon before the button text. Defaults to `Icon` component.
 * - `<FilterChipToggle.TrailingIcon>`: (optional) Renders a trailing icon after the button text. Defaults to `Icon` component.
 * - `<FilterChipToggle.Spinner>`: (optional) Renders a spinner icon when the button is in a loading state. Defaults to `Spinner` component and will show when `isLoading` is true.
 *
 * ### Functional properties
 * - `pressed`: Controls the pressed state of the button. If `true`, the button is in the `on` state.
 * - `defaultPressed`: The default pressed state of the button. If `true`, the button is in the `on` state by default.
 * - `onPressedChange`: A callback function that is called when the pressed state of the button changes.
 * - `isLoading`: If true, the button will display a loading spinner icon and click functionality will be disabled.
 * - `showCheckOnPressed`: If true, a check icon will be displayed when the button is pressed. It will replace the leading icon slot (only when pressed).
 */
export const FilterChipToggle = Object.assign(FilterChipToggleBase, {
  LeadingIcon: FilterChipToggleLeadingIconSlot,
  TrailingIcon: FilterChipToggleTrailingIconSlot,
  Spinner: FilterChipToggleSpinnerSlot,
});
